import type { CloseGuideSource, OpenGuideSource } from "@expert/solve-tools";

import { CustomerProfile, usePushDiscoveryEngagementToTimeline, useVerifiedCustomerStore } from "@expert/sales-tools";
import { Flex, Tabs } from "@mantine/core";
import clsx from "clsx";
import { type ReactNode, useRef } from "react";

import { GuidedFlowsPanel } from "@/components/workspace/GuidedFlowsPanel";
import { useSessionId } from "@/sdk";

import type { RightTopTab } from "./sidecarTabEventBus";

import { AssistTab } from "./AssistTab";
import { CustomerTab } from "./CustomerTab";
import { OffersTab } from "./OffersTab";
import styles from "./Sidecar.module.css";
import tabPanelStyles from "./SidecarTabPanel.module.css";
import { useSidecarTabState } from "./useSidecarTabState";

interface Props {
    customerSession: boolean;
    openGuide: (source: OpenGuideSource) => void;
    closeGuide: (source: CloseGuideSource) => void;
    guide: ReactNode;
    areGuidesAvailable: boolean;
    isGuideOpen: boolean;
    /**
     * Whether the AI Assist tab is shown. Defaults to `true` (voice always shows it). Chat passes
     * `isChatExpertAssistEnabled` so the tab is hidden when chat expert-assist is off.
     */
    showAssistTab?: boolean;
    /**
     * Whether the Offers tab is shown. Defaults to `true` (voice). Chat passes `isSalesEnabled` so
     * Offers is gated on sales — independently of the Customer tab, which stays available for any
     * customer session so the customer can be confirmed even when sales is off.
     */
    showOffersTab?: boolean;
    /**
     * Optional per-consumer tab persistence (chat). `initialTab` is restored when selectable (on mount,
     * or once its flags load); `onTabChange` reports the selected tab to persist. Voice omits both →
     * ephemeral tab state.
     */
    initialTab?: RightTopTab;
    onTabChange?: (tab: RightTopTab) => void;
    /**
     * Whether to play the tab/panel enter animations on mount. Defaults to `true` (voice). Chat passes
     * `false` for chats it has shown before so switching back is instant (the tabs otherwise stay
     * invisible ~1.2s while the staggered `tabEnter` runs). Frozen at mount — see below.
     */
    animateOnEnter?: boolean;
}

/**
 * Shared right-top tab bar for voice and chat. Per-product differences go through props with
 * voice-preserving defaults (`showAssistTab`, `showOffersTab`, `animateOnEnter`, `initialTab`,
 * `onTabChange`): voice omits them and keeps today's behavior, chat opts in.
 *
 * SPLIT TRIGGER: this stays shared only while the products differ by *toggles*. When voice and chat
 * need a different tab *set* or different panel *content*, stop adding flags here — switch to
 * consumer-supplied tab descriptors (each consumer passes its own tabs) so neither can regress the other.
 */
export function Sidecar({
    customerSession,
    openGuide,
    closeGuide,
    guide,
    areGuidesAvailable,
    isGuideOpen,
    showAssistTab = true,
    showOffersTab = true,
    initialTab,
    onTabChange,
    animateOnEnter = true,
}: Props) {
    // Freeze at mount: the chat consumer's "seen" signal (savedRightTopTab) flips to defined within
    // this mount (the tab is persisted on mount), so a live prop would cut off the first-visit
    // animation. The component remounts per chat (key=activeChatId), so this re-captures per chat.
    const animateOnEnterRef = useRef(animateOnEnter);
    const sessionId = useSessionId();
    const isVerified = useVerifiedCustomerStore(
        (state) => state.getVerifiedCustomer(sessionId).isCustomerVerificationComplete,
    );

    // Owned here, outside <Tabs>, so this once-per-session push never depends on tab mount mode.
    // #7241 restores `keepMountedMode="display-none"`, which keeps hidden panels' effects alive and
    // addresses the deferral on its own — but OffersTab still swaps CustomerOffers out for
    // ProductSaleFlowPanel mid-sale, which unmounts a pusher owned by that panel even while the tab
    // is open. Keeping it here is independent of both.
    // `enabled: showOffersTab` — Chat hides Offers when sales is off but keeps the Customer tab,
    // and CustomerTab runs `useEligibility`, so the offer store fills regardless. Without this the
    // hoist would push Discovery guidance into a sales-disabled chat.
    usePushDiscoveryEngagementToTimeline({ enabled: showOffersTab });

    const { activeTab, handleTabChange, tabsJustEnabled, hasGuideNotification } = useSidecarTabState({
        showAssistTab,
        customerSession,
        isVerified,
        showOffersTab,
        areGuidesAvailable,
        isGuideOpen,
        openGuide,
        closeGuide,
        initialTab,
        onTabChange,
    });

    const showGuidedFlows = areGuidesAvailable || isGuideOpen;

    return (
        <Flex
            pos="absolute"
            inset={0}
            className={clsx(styles.root, !animateOnEnterRef.current && styles.noEnterAnimations)}
        >
            {isVerified && (
                <div
                    className={clsx(styles.customerHeader, animateOnEnterRef.current && tabPanelStyles.fadeIn)}
                    data-testid="right-top-customer-header"
                >
                    <CustomerProfile />
                </div>
            )}
            <Tabs
                variant="unstyled"
                value={activeTab}
                onChange={handleTabChange}
                // Inactive panels stay mounted (Mantine default), but the default "activity" mode
                // suspends their effects via React <Activity>, stalling each panel's data work
                // (eligibility fetch, offers polling/pushes, Gaia listeners) until reopened.
                // "display-none" hides with CSS instead, keeping effects alive.
                keepMounted
                keepMountedMode="display-none"
                classNames={{
                    root: styles.tabsRoot,
                    list: styles.tabsList,
                    tab: styles.tabsTab,
                    tabLabel: styles.tabsTabLabel,
                    panel: styles.tabsPanel,
                }}
            >
                <Tabs.List grow>
                    {/* Chat hides AI Assist when expert-assist is off (showAssistTab=false). */}
                    {showAssistTab && <Tabs.Tab value="ai-assist">AI Assist</Tabs.Tab>}
                    <Tabs.Tab value="customer" disabled={!customerSession} data-pulse={tabsJustEnabled || undefined}>
                        Customer
                    </Tabs.Tab>
                    {/* Chat hides Offers when sales is off (showOffersTab=false); Customer stays available. */}
                    {showOffersTab && (
                        <Tabs.Tab
                            value="offers"
                            disabled={!customerSession || !isVerified}
                            data-pulse={tabsJustEnabled || undefined}
                        >
                            Offers
                        </Tabs.Tab>
                    )}
                    <Tabs.Tab
                        value="guided-flows"
                        disabled={!areGuidesAvailable}
                        data-pulse={tabsJustEnabled || undefined}
                        data-hidden-tab={!showGuidedFlows || undefined}
                        data-has-notification={hasGuideNotification || undefined}
                    >
                        Guided flows
                    </Tabs.Tab>
                </Tabs.List>

                {showAssistTab && (
                    <Tabs.Panel value="ai-assist">
                        <AssistTab />
                    </Tabs.Panel>
                )}

                <Tabs.Panel value="customer">{customerSession ? <CustomerTab /> : null}</Tabs.Panel>

                {showOffersTab && (
                    <Tabs.Panel value="offers">{customerSession && isVerified ? <OffersTab /> : null}</Tabs.Panel>
                )}

                <Tabs.Panel value="guided-flows">
                    {isGuideOpen ? (
                        <Flex pos="absolute" inset={0} className={tabPanelStyles.scrollableRoot}>
                            <Flex className={tabPanelStyles.column}>
                                <GuidedFlowsPanel guide={guide} />
                            </Flex>
                        </Flex>
                    ) : null}
                </Tabs.Panel>
            </Tabs>
        </Flex>
    );
}
